import type {
  CaptureConsoleConfig,
  LoggerOptions,
  ResolvedCaptureConsole,
  ResolvedLoggerOptions,
} from './types.js';

const DEFAULT_CAPTURE: ResolvedCaptureConsole = {
  error: true,
  warn: true,
  info: false,
  log: false,
  debug: false,
};

function resolveCapture(input: CaptureConsoleConfig | undefined): ResolvedCaptureConsole {
  if (input === undefined) return { ...DEFAULT_CAPTURE };
  if (input === true) return { error: true, warn: true, info: true, log: true, debug: true };
  if (input === false) return { error: false, warn: false, info: false, log: false, debug: false };
  return { ...DEFAULT_CAPTURE, ...input };
}

function trimTrailingSlash(url: string): string {
  return url.endsWith('/') ? url.slice(0, -1) : url;
}

function noop(): void {
  /* swallow */
}

export function resolveOptions(opts: LoggerOptions): ResolvedLoggerOptions {
  if (!opts || typeof opts.baseUrl !== 'string' || opts.baseUrl.length === 0) {
    throw new Error('[evam-logger] baseUrl is required');
  }

  const fetchImpl: typeof fetch | undefined =
    opts.fetch ?? (typeof fetch === 'function' ? fetch.bind(globalThis) : undefined);

  const resolved: ResolvedLoggerOptions = {
    baseUrl: trimTrailingSlash(opts.baseUrl),
    captureConsole: resolveCapture(opts.captureConsole),
    captureGlobalErrors: opts.captureGlobalErrors ?? true,
    captureBreadcrumbs: opts.captureBreadcrumbs ?? false,
    maxBreadcrumbs: opts.maxBreadcrumbs ?? 50,

    endpointPath: opts.endpointPath ?? '/api/v1/logger/log',
    credentials: opts.credentials ?? 'include',
    csrfCookieName: opts.csrfCookieName ?? 'csrf_token',
    csrfHeaderName: opts.csrfHeaderName ?? 'X-CSRF-Token',

    enabled: opts.enabled ?? true,
    sampleRate: opts.sampleRate ?? 1,
    onError: opts.onError ?? noop,
    maxQueueSize: opts.maxQueueSize ?? 100,
    maxConcurrent: opts.maxConcurrent ?? 4,
    maxRetries: opts.maxRetries ?? 2,
    retryBaseDelayMs: opts.retryBaseDelayMs ?? 500,
    retryStatusCodes: opts.retryStatusCodes ?? [408, 429, 500, 502, 503, 504],
    flushOnUnload: opts.flushOnUnload ?? true,
    dedupeWindowMs: opts.dedupeWindowMs ?? 0,

    maxMessageLength: opts.maxMessageLength ?? 8192,
    maxArgsBytes: opts.maxArgsBytes ?? 32768,
    debug: opts.debug ?? false,
  };

  if (fetchImpl) resolved.fetch = fetchImpl;
  if (opts.app !== undefined) resolved.app = opts.app;
  if (opts.release !== undefined) resolved.release = opts.release;
  if (opts.environment !== undefined) resolved.environment = opts.environment;
  if (opts.user !== undefined) resolved.user = opts.user;
  if (opts.tenant !== undefined) resolved.tenant = opts.tenant;
  if (opts.context !== undefined) resolved.context = { ...opts.context };
  if (opts.tags !== undefined) resolved.tags = [...opts.tags];
  if (opts.getAuthToken !== undefined) resolved.getAuthToken = opts.getAuthToken;
  if (opts.onAuthError !== undefined) resolved.onAuthError = opts.onAuthError;
  if (opts.beforeSend !== undefined) resolved.beforeSend = opts.beforeSend;

  return resolved;
}
